import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import { DayBarChart } from "~/components/day-bar-chart";
import {
  type DayCount,
  fillDateGaps,
  chartDateRange,
} from "~/lib/shared/day-chart";
import { violet } from "~/lib/ui";

export const getEngagementsByDay = createServerFn({ method: "GET" }).handler(
  async (): Promise<Array<DayCount>> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { engagements } = await import("~/../drizzle/schema");
    const { sql, and, gte, lt } = await import("drizzle-orm");

    const { rangeStart, rangeEnd } = chartDateRange(60, 0);
    const db = await getDBWithInit();

    const rows = await db
      .select({
        date: sql<string>`DATE(${engagements.createdAt})`.as("date"),
        count: sql<number>`COUNT(*)::int`.as("count"),
      })
      .from(engagements)
      .where(
        and(
          gte(engagements.createdAt, rangeStart),
          lt(engagements.createdAt, rangeEnd),
        ),
      )
      .groupBy(sql`DATE(${engagements.createdAt})`)
      .orderBy(sql`DATE(${engagements.createdAt})`);

    return fillDateGaps(rows, {
      daysBefore: 60,
      daysAfter: 0,
      pastColor: violet[600],
      futureColor: violet[400],
    });
  },
);

function EngagementsChartPage() {
  const { data } = Route.useLoaderData();
  return (
    <DayBarChart
      data={data}
      noun="Coaching Programs"
      rangeLabel="last 60 days"
      tickInterval={10}
    />
  );
}

export const Route = createFileRoute("/embed/engagements-chart/")({
  head: () => ({
    title: "Coaching Programs Chart - Dream Coach Match",
    meta: [
      {
        name: "description",
        content:
          "Embedded chart showing new coaching programs created over time",
      },
    ],
  }),
  loader: async () => {
    const data = await getEngagementsByDay();
    return { data };
  },
  component: EngagementsChartPage,
});
